import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { useData } from '../../contexts/DataContext';
import ElevatedButton from '../Layout/ElevatedButton';
import { getAcademicSemesterOptions, getSemesterDisplayLabel } from '../../utils/semesters';

const Form = styled.form`
  max-width: 400px;
  margin: 2rem auto;
  padding: 2rem;
  border: 1px solid ${props => props.theme.buttonPrimary};
  border-radius: 8px;
  background: ${props => props.theme.cardBackground};
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
  color: ${props => props.theme.text};
`;

const Input = styled.input`
  width: 100%;
  padding: 0.5rem 0.75rem;
  margin: 0.5rem 0;
  border: 1px solid ${props => props.theme.inputBorder};
  border-radius: 4px;
  font-size: 0.875rem;
  background: ${props => props.theme.inputBackground};
  color: ${props => props.theme.text};
  transition: all 0.2s ease;
  box-sizing: border-box;

  &::placeholder {
    color: ${props => props.theme.textMuted};
  }

  &:focus {
    outline: none;
    border-color: ${props => props.theme.buttonPrimary};
    box-shadow: 0 0 0 2px rgba(88, 101, 242, 0.1);
  }
`;

const Textarea = styled.textarea`
  width: 100%;
  padding: 0.5rem 0.75rem;
  margin: 0.5rem 0;
  border: 1px solid ${props => props.theme.inputBorder};
  border-radius: 4px;
  font-size: 0.875rem;
  background: ${props => props.theme.inputBackground};
  color: ${props => props.theme.text};
  resize: vertical;
  transition: all 0.2s ease;
  box-sizing: border-box;
  min-height: 80px;

  &::placeholder {
    color: ${props => props.theme.textMuted};
  }

  &:focus {
    outline: none;
    border-color: ${props => props.theme.buttonPrimary};
    box-shadow: 0 0 0 2px rgba(88, 101, 242, 0.1);
  }
`;

const Select = styled.select`
  width: 100%;
  padding: 0.5rem 0.75rem;
  margin: 0.5rem 0;
  border: 1px solid ${props => props.theme.inputBorder};
  border-radius: 4px;
  font-size: 0.875rem;
  background: ${props => props.theme.inputBackground};
  color: ${props => props.theme.text};
  transition: all 0.2s ease;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: ${props => props.theme.buttonPrimary};
    box-shadow: 0 0 0 2px rgba(88, 101, 242, 0.1);
  }

  option {
    background: ${props => props.theme.inputBackground};
    color: ${props => props.theme.text};
  }
`;

const ColorInput = styled.input`
  width: 100%;
  padding: 0.5rem 0.75rem;
  margin: 0.5rem 0;
  border: 1px solid ${props => props.theme.inputBorder};
  border-radius: 4px;
  font-size: 0.875rem;
  background: ${props => props.theme.inputBackground};
  color: ${props => props.theme.text};
  transition: all 0.2s ease;
  box-sizing: border-box;
  height: 38px;

  &:focus {
    outline: none;
    border-color: #5865f2;
    box-shadow: 0 0 0 2px rgba(88, 101, 242, 0.1);
  }
`;

const CourseForm = ({ course, onSubmit, isEditing = false }) => {
  const [name, setName] = useState(course?.name || '');
  const [description, setDescription] = useState(course?.description || '');
  const [instructor, setInstructor] = useState(course?.instructor || '');
  const [location, setLocation] = useState(course?.location || 'online');
  const [classNumber, setClassNumber] = useState(course?.class_number || '');
  const [courseCode, setCourseCode] = useState(course?.course_code || '');
  const [color, setColor] = useState(course?.color || '#5865f2');
  const [semester, setSemester] = useState(getSemesterDisplayLabel(course?.semester || 'Fall'));
  const [year, setYear] = useState(course?.year || new Date().getFullYear());
  const [credits, setCredits] = useState(course?.credits || 3);
  const { addCourse, updateCourse, settings } = useData();

  useEffect(() => {
    setSemester(getSemesterDisplayLabel(course?.semester || 'Fall', settings));
  }, [course?.semester, settings]);

  const semesterOptions = getAcademicSemesterOptions(settings);

  const handleSubmit = (e) => {
    e.preventDefault();
    const courseData = {
      name,
      description,
      instructor,
      location,
      class_number: location === 'online' ? '' : classNumber,
      course_code: courseCode,
      color,
      semester,
      year: parseInt(year),
      credits: parseInt(credits) || 3
    };

    if (isEditing && course) {
      updateCourse(course._id, courseData);
    } else {
      addCourse(courseData);
    }

    // Reset form
    setName('');
    setDescription('');
    setInstructor('');
    setLocation('online');
    setClassNumber('');
    setCourseCode('');
    setColor('#5865f2');
    setSemester('Fall');
    setYear(new Date().getFullYear());
    setCredits(3);

    if (onSubmit) onSubmit();
  };

  return (
    <Form onSubmit={handleSubmit}>
      <h2 style={{ color: 'var(--color-text)', textAlign: 'center', textShadow: '0 2px 4px rgba(0, 0, 0, 0.3)' }}>
        {isEditing ? 'Edit Course' : 'Add Course'}
      </h2>
      <Input
        type="text"
        placeholder="Course Name"
        value={name}
        onChange={(e) => setName(e.target.value)}
        required
      />
      <Input
        type="text"
        placeholder="Course Code (e.g., CS101)"
        value={courseCode}
        onChange={(e) => setCourseCode(e.target.value)}
        required
      />
      <Select value={semester} onChange={(e) => setSemester(e.target.value)}>
        {semesterOptions.map(option => (
          <option key={option} value={option}>{option}</option>
        ))}
      </Select>
      <Input
        type="number"
        placeholder="Year (e.g., 2024)"
        value={year}
        onChange={(e) => setYear(e.target.value)}
        min="2020"
        max="2030"
        required
      />
      <Input
        type="number"
        placeholder="Credits (e.g., 3)"
        value={credits}
        onChange={(e) => setCredits(e.target.value)}
        min="0"
        max="12"
        step="0.5"
        required
      />
      <Input
        type="text"
        placeholder="Instructor Name"
        value={instructor}
        onChange={(e) => setInstructor(e.target.value)}
      />
      <Select value={location} onChange={(e) => setLocation(e.target.value)}>
        <option value="online">Online</option>
        <option value="in-person">In-Person</option>
      </Select>
      {location === 'in-person' && (
        <Input
          type="text"
          placeholder="Class Number/Room (e.g., Room 101)"
          value={classNumber}
          onChange={(e) => setClassNumber(e.target.value)}
        />
      )}
      <ColorInput
        type="color"
        value={color}
        onChange={(e) => setColor(e.target.value)}
        title="Choose course color"
      />
      <Textarea
        placeholder="Description"
        value={description}
        onChange={(e) => setDescription(e.target.value)}
        rows="4"
      />
      <ElevatedButton type="submit" style={{ marginTop: '1rem', marginLeft: 'auto', display: 'block' }}>
        {isEditing ? 'Update Course' : 'Add Course'}
      </ElevatedButton>
    </Form>
  );
};

export default CourseForm;